import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, FilePlus2, Gauge, Loader2 } from 'lucide-react';
import { ResumeBuilderSsoButton } from './ResumeBuilderSsoButton';
import { ResumeBuilderResumeItem } from './ResumeBuilderResumeItem';
import { resumeMarketingService } from '../services/resumeMarketing.service';

function formatResumeDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Collapsible "Resume creation" section for Resume Marketing sidebar (Employee / TL).
 */
export function ResumeCreationSidebarDropdown({
  returnPath,
  open,
  onToggle,
  variant = 'employee',
}) {
  const location = useLocation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(true);
  const isControlled = typeof open === 'boolean';
  const isOpen = isControlled ? open : internalOpen;

  const fetchResumes = useCallback(async () => {
    try {
      const result = await resumeMarketingService.getResumeBuilderResumes();
      setResumes(Array.isArray(result?.data) ? result.data : []);
    } catch {
      setResumes([]);
    } finally {
      setLoadingResumes(false);
    }
  }, []);

  useEffect(() => {
    fetchResumes();
  }, [fetchResumes]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        fetchResumes();
      }
    };
    window.addEventListener('focus', fetchResumes);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', fetchResumes);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [fetchResumes]);

  const handleToggle = (event) => {
    const next = event.currentTarget.open;
    if (onToggle) onToggle(next);
    if (!isControlled) setInternalOpen(next);
    if (next) fetchResumes();
  };

  const isTl = variant === 'tl';
  const nestedButtonClass = isTl
    ? 'flex w-full items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors disabled:opacity-60'
    : 'my-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-content-secondary transition-colors hover:bg-gray-100 disabled:opacity-60';
  const nestedLinkClass = isTl
    ? 'flex w-full items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors'
    : 'my-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-content-secondary transition-colors hover:bg-gray-100';
  const isAtsActive = location.pathname === '/resume-marketing/ats-checker';

  return (
    <details
      className="mx-2 mt-3"
      open={isOpen}
      onToggle={handleToggle}
    >
      <summary
        className={
          isTl
            ? 'cursor-pointer flex items-center px-4 py-2.5 rounded-lg text-content-secondary hover:bg-gray-100 transition-colors'
            : 'flex cursor-pointer list-none items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-content-secondary transition-colors hover:bg-gray-100 [&::-webkit-details-marker]:hidden'
        }
      >
        <FilePlus2
          className={
            isTl
              ? 'w-5 h-5 mr-3 flex-shrink-0 text-content-muted'
              : 'h-5 w-5 shrink-0 text-content-muted'
          }
        />
        <span className={`truncate ${isTl ? '' : 'font-medium'}`}>Resume creation</span>
        <ChevronDown
          className={`ml-auto shrink-0 text-content-muted transition-transform ${
            isTl ? 'w-4 h-4' : 'h-4 w-4'
          } ${isOpen ? 'rotate-180' : ''}`}
        />
      </summary>
      <div className={isTl ? 'ml-6 mt-1 space-y-1' : 'ml-2 mt-1 space-y-0.5 border-l border-default/80 pl-3'}>
        <ResumeBuilderSsoButton
          returnPath={returnPath}
          nested={!isTl}
          className={nestedButtonClass}
        />

        <Link
          to="/resume-marketing/ats-checker"
          className={`${nestedLinkClass} ${
            isAtsActive ? 'bg-blue-50 font-medium text-blue-600 ring-1 ring-blue-100' : ''
          }`}
        >
          <Gauge
            className={`${isTl ? 'w-5 h-5 mr-3' : 'h-4 w-4'} shrink-0 ${
              isAtsActive ? 'text-blue-600' : 'text-content-muted'
            }`}
          />
          <span className="truncate">ATS score checker</span>
        </Link>

        <div className={isTl ? 'pt-2' : 'pt-1'}>
          <p
            className={
              isTl
                ? 'px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-muted'
                : 'px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-muted'
            }
          >
            My resumes
          </p>

          {loadingResumes ? (
            <div
              className={
                isTl
                  ? 'flex items-center gap-2 px-4 py-2 text-xs text-content-muted'
                  : 'flex items-center gap-2 px-3 py-2 text-xs text-content-muted'
              }
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading resumes…
            </div>
          ) : resumes.length === 0 ? (
            <p
              className={
                isTl
                  ? 'px-4 py-2 text-xs text-content-muted'
                  : 'px-3 py-2 text-xs text-content-muted'
              }
            >
              No resumes yet. Create one to get started.
            </p>
          ) : (
            resumes.map((resume) => {
              const displayTitle =
                resume.candidateName?.trim() ||
                resume.title?.trim() ||
                'Untitled resume';
              const updatedLabel = formatResumeDate(resume.updatedAt);
              const label = updatedLabel
                ? `${displayTitle} · ${updatedLabel}`
                : displayTitle;

              return (
                <ResumeBuilderResumeItem
                  key={resume.id}
                  resume={{ ...resume, label }}
                  returnPath={returnPath}
                  nested={!isTl}
                  isTl={isTl}
                  buttonClassName={nestedButtonClass}
                  onDeleted={() => fetchResumes()}
                />
              );
            })
          )}
        </div>
      </div>
    </details>
  );
}
